import { useState, useEffect } from 'react';
import { Card, Button, Spinner, Alert, ButtonGroup } from 'react-bootstrap';
import Form from '@rjsf/core';
import validator from '@rjsf/validator-ajv8';
import { RJSFSchema, IconButtonProps, FieldTemplateProps } from '@rjsf/utils';
import { WorkloadConfig } from '../types';
import { ApiService } from '../services/api';
import { EnvironmentTabs } from './EnvironmentTabs';
import './ValidationStyles.css';

// Custom button templates for better styling
function AddButton(props: IconButtonProps) {
  return (
    <Button
      variant="success"
      size="sm"
      onClick={props.onClick}
      disabled={props.disabled}
      className="mt-2"
    >
      <i className="bi bi-plus-circle me-1"></i>
      Add {props.title || 'Item'}
    </Button>
  );
}

function RemoveButton(props: IconButtonProps) {
  return (
    <Button
      variant="danger"
      size="sm"
      onClick={props.onClick}
      disabled={props.disabled}
      className="ms-2"
    >
      <i className="bi bi-trash me-1"></i>
      Remove
    </Button>
  );
}

// Custom array field template for better visual separation
function ArrayFieldTemplate(props: any) {
  return (
    <div className="mb-4">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <label className="form-label fw-bold">{props.title}</label>
        {props.canAdd && (
          <Button
            variant="success"
            size="sm"
            onClick={props.onAddClick}
            disabled={props.disabled || props.readonly}
          >
            <i className="bi bi-plus-circle me-1"></i>
            Add {props.title}
          </Button>
        )}
      </div>
      {props.items && props.items.length > 0 && (
        <div className="border rounded p-3 bg-light">
          {props.items.map((element: any) => (
            <Card key={element.key} className="mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start">
                  <div className="flex-grow-1">{element.children}</div>
                  {element.hasRemove && (
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={element.onDropIndexClick(element.index)}
                      disabled={element.disabled || element.readonly}
                    >
                      <i className="bi bi-trash"></i>
                    </Button>
                  )}
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
      )}
      {props.items && props.items.length === 0 && (
        <div className="text-muted fst-italic">
          No {props.title?.toLowerCase()} added yet. Click "Add {props.title}" to create one.
        </div>
      )}
    </div>
  );
}

// Custom object field template for nested objects
function ObjectFieldTemplate(props: any) {
  const isTopLevel = props.idSchema.$id === 'root';
  
  if (isTopLevel) {
    return <div>{props.properties.map((prop: any) => prop.content)}</div>;
  }

  return (
    <Card className="mb-3">
      <Card.Header className="bg-white">
        <strong>{props.title}</strong>
        {props.description && (
          <small className="text-muted d-block">{props.description}</small>
        )}
      </Card.Header>
      <Card.Body>
        {props.properties.map((prop: any) => prop.content)}
      </Card.Body>
    </Card>
  );
}

// Custom field template to show inline validation errors
function FieldTemplate(props: FieldTemplateProps) {
  const {
    id,
    classNames,
    label,
    help,
    required,
    errors,
    children,
    displayLabel,
    rawErrors,
  } = props;

  // Check for errors in multiple ways (RJSF can pass errors differently)
  const errorList = rawErrors || (errors && errors.props && errors.props.errors) || [];
  const hasErrors = errorList && errorList.length > 0;
  const fieldClassName = `form-group ${classNames} ${hasErrors ? 'field-error' : ''}`;

  return (
    <div className={fieldClassName}>
      {displayLabel && label && (
        <label htmlFor={id} className={`form-label ${required ? 'required' : ''}`}>
          {label}
        </label>
      )}
      {children}
      {hasErrors && (
        <div className="field-error-message">
          {errorList.map((error: string, index: number) => (
            <div key={index} className="error-detail">
              {error}
            </div>
          ))}
        </div>
      )}
      {help}
    </div>
  );
}

const customTemplates = {
  ButtonTemplates: {
    AddButton,
    RemoveButton,
  },
  ArrayFieldTemplate,
  ObjectFieldTemplate,
  FieldTemplate,
};

interface ConfigFormProps {
  workloadName?: string;
  initialData?: WorkloadConfig;
  onSave: (config: WorkloadConfig) => void;
  onCancel: () => void;
}

export function ConfigForm({ workloadName, initialData, onSave, onCancel }: ConfigFormProps) {
  const [schema, setSchema] = useState<RJSFSchema | null>(null);
  const [environmentSchema, setEnvironmentSchema] = useState<RJSFSchema | null>(null);
  const [formData, setFormData] = useState<WorkloadConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<any>(null);
  const [showValidation, setShowValidation] = useState(false);

  useEffect(() => {
    loadData();
  }, [workloadName, initialData]);

  const enhanceSchemaWithInvoiceUnits = async (schema: any) => {
    console.log('🔍 Starting schema enhancement...');
    try {
      console.log('📞 Calling ApiService.getInvoiceUnits()...');
      const invoiceUnits = await ApiService.getInvoiceUnits();
      console.log('✅ Received invoice units:', invoiceUnits);
      
      if (invoiceUnits.length > 0 && schema?.properties?.environments?.items?.properties?.customizations?.items?.allOf) {
        console.log('✅ Schema has correct structure, proceeding with enhancement');
        const updatedSchema = JSON.parse(JSON.stringify(schema)); // Deep clone
        
        // Find invoice-unit customization in allOf conditions
        const customizationsAllOf = updatedSchema.properties.environments.items.properties.customizations.items.allOf;
        console.log('🔍 Searching for invoice-unit condition in allOf:', customizationsAllOf.length, 'conditions');
        
        const invoiceUnitCondition = customizationsAllOf.find(
          (item: any) => item.if?.properties?.type?.const === 'invoice-unit'
        );
        console.log('🔍 Found invoice-unit condition:', !!invoiceUnitCondition);
        
        if (invoiceUnitCondition?.then?.properties?.parameters?.properties?.invoiceUnitName) {
          console.log('✅ Found invoiceUnitName property, updating to enum');
          // Update to enum with invoice unit names
          invoiceUnitCondition.then.properties.parameters.properties.invoiceUnitName = {
            type: 'string',
            title: 'Invoice Unit Name',
            description: 'Select an invoice unit from the management account',
            enum: invoiceUnits.map((u: {name: string, arn: string, description: string}) => u.name),
            enumNames: invoiceUnits.map((u: {name: string, arn: string, description: string}) => `${u.name}${u.description ? ` - ${u.description}` : ''}`),
          };
          
          console.log('✅ Enhanced schema with', invoiceUnits.length, 'invoice units');
          console.log('📋 Updated invoiceUnitName property:', invoiceUnitCondition.then.properties.parameters.properties.invoiceUnitName);
        } else {
          console.warn('⚠️ Could not find invoiceUnitName property in schema');
        }
        
        return updatedSchema;
      } else {
        console.warn('⚠️ Schema enhancement skipped:', {
          hasInvoiceUnits: invoiceUnits.length > 0,
          hasCorrectStructure: !!schema?.properties?.environments?.items?.properties?.customizations?.items?.allOf
        });
      }
    } catch (error) {
      console.error('❌ Failed to enhance schema with invoice units:', error);
    }
    
    // Return original schema if enhancement fails
    console.log('↩️ Returning original schema');
    return schema;
  };

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      const schemaData = await ApiService.getSchema();
      
      // Enhance schema with invoice units dropdown
      const enhancedSchemaData = await enhanceSchemaWithInvoiceUnits(schemaData);
      
      // Extract environment schema from the main schema
      const envSchema = (enhancedSchemaData as any).properties?.environments?.items;
      if (envSchema) {
        setEnvironmentSchema(envSchema as RJSFSchema);
      }
      
      // Use the full schema (don't remove environments)
      setSchema(enhancedSchemaData as RJSFSchema);

      // Use initialData if provided (coming back from preview)
      if (initialData) {
        setFormData(initialData);
      } else if (workloadName) {
        const config = await ApiService.getConfig(workloadName);
        setFormData(config);
      } else {
        setFormData({
          workloadName: '',
          schemaVersion: '1.0',
          gitlabProjectPath: '',
          gitlabRepository: {
            createNew: true,
          },
          description: '',
          environments: [],
        } as WorkloadConfig);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (_data: any, event: any) => {
    // Check if there are validation errors
    if (event && event.errors && event.errors.length > 0) {
      setValidationErrors(event.errors);
      setShowValidation(true);
      // Scroll to top to show error summary
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    
    // Clear validation errors and proceed
    setValidationErrors(null);
    setShowValidation(false);
    
    // The formData already has environments from the state
    onSave(formData!);
  };
  
  const handleError = (errors: any) => {
    setValidationErrors(errors);
    setShowValidation(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3">Loading form...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error}</p>
        <ButtonGroup>
          <Button variant="outline-danger" onClick={loadData}>
            Retry
          </Button>
          <Button variant="outline-secondary" onClick={onCancel}>
            Cancel
          </Button>
        </ButtonGroup>
      </Alert>
    );
  }

  if (!schema || !formData || !environmentSchema) {
    return <Alert variant="warning">No data available</Alert>;
  }

  return (
    <Card>
      <Card.Header>
        <h4 className="mb-0">
          {workloadName ? `Edit ${workloadName}` : 'Create New Configuration'}
        </h4>
      </Card.Header>
      <Card.Body>
        {showValidation && validationErrors && validationErrors.length > 0 && (
          <Alert variant="danger" dismissible onClose={() => setShowValidation(false)}>
            <Alert.Heading>
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              Validation Errors ({validationErrors.length})
            </Alert.Heading>
            <p className="mb-2">Please fix the following errors before saving:</p>
            <ul className="mb-0">
              {validationErrors.slice(0, 10).map((error: any, index: number) => (
                <li key={index}>
                  <strong>{error.property || 'Field'}:</strong> {error.message}
                </li>
              ))}
              {validationErrors.length > 10 && (
                <li className="text-muted">...and {validationErrors.length - 10} more errors</li>
              )}
            </ul>
          </Alert>
        )}
        
        <Form
          id="config-form"
          schema={schema}
          uiSchema={{
            environments: {
              'ui:field': () => null  // Don't render environments field
            },
            gitlabRepository: {
              'ui:field': () => null  // Don't render old gitlabRepository field
            }
          }}
          formData={formData}
          validator={validator}
          onSubmit={handleSubmit}
          onError={handleError}
          onChange={(changeData) => {
            if (changeData.formData) {
              // Remove gitlabRepository if it exists (old field)
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              const { gitlabRepository, ...cleanData } = changeData.formData;
              setFormData({ ...cleanData, environments: formData?.environments || [] });
              // If we're in validation mode, clear errors for valid fields
              if (showValidation && changeData.errors && changeData.errors.length === 0) {
                setValidationErrors(null);
              }
            }
          }}
          templates={customTemplates}
          liveValidate={showValidation}
          showErrorList={false}
          noHtml5Validate
        >
          <button type="submit" style={{ display: 'none' }} />
        </Form>
        
        {/* Separate environment tabs section */}
        <EnvironmentTabs
          environments={formData.environments || []}
          environmentSchema={environmentSchema}
          workloadName={formData.workloadName || ''}
          onChange={(environments) => {
            setFormData({ ...formData, environments });
          }}
          showValidation={showValidation}
        />

        <div className="d-flex gap-2 mt-4 pt-3 border-top">
          <Button variant="primary" type="submit" form="config-form">
            <i className="bi bi-check-circle me-2"></i>
            Save Configuration
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            <i className="bi bi-x-circle me-2"></i>
            Cancel
          </Button>
        </div>
      </Card.Body>
    </Card>
  );
}
